import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 6;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const [state, setState] = useState({ status: "polling", message: "Confirming your payment..." });
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState({ status: "error", message: "Missing session id." });
      return;
    }

    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refreshMe();
          setState({ status: "success", message: "Subscription activated." });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "error", message: "Payment session expired." });
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          setState({ status: "pending", message: "Still processing. We'll update your subscription as soon as Stripe confirms." });
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL);
      } catch {
        if (attempts.current >= MAX_ATTEMPTS) {
          setState({ status: "error", message: "Couldn't confirm payment status. Contact support if you were charged." });
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [params]);

  const Icon =
    state.status === "success" ? CheckCircle2 :
    state.status === "error" ? XCircle :
    state.status === "pending" ? Sparkles : Loader2;

  const iconColor =
    state.status === "success" ? "text-green-700 bg-green-100" :
    state.status === "error" ? "text-red-600 bg-red-100" :
    "text-sky-700 bg-sky-100";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f9f9f8] p-6" data-testid="payment-success-page">
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-10 text-center">
        <div className={`w-14 h-14 rounded-full ${iconColor} mx-auto flex items-center justify-center`}>
          <Icon className={`w-7 h-7 ${state.status === "polling" ? "animate-spin" : ""}`} />
        </div>
        <h1 className="font-display mt-5 text-3xl font-semibold text-gray-900" data-testid="payment-status-title">
          {state.status === "success" ? "You're all set!" :
           state.status === "error" ? "Payment issue" :
           state.status === "pending" ? "Almost there" :
           "Confirming..."}
        </h1>
        <p className="mt-3 text-gray-600" data-testid="payment-status-message">{state.message}</p>
        <div className="mt-8 flex flex-col gap-2">
          <Button onClick={() => navigate("/dashboard")} className="w-full h-11 bg-green-800 hover:bg-green-900 text-white" data-testid="payment-go-dashboard">
            Go to dashboard
          </Button>
          {state.status === "error" && (
            <Button variant="outline" onClick={() => navigate("/pricing")} className="w-full h-11" data-testid="payment-retry">
              Back to pricing
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
